// built-from: /shared-process-spine v1 impl:shared-process-spine role:implementation
import { useCallback, useEffect, useRef, useState } from 'react';
import { sharedCommandId, type SharedProcess } from './useSharedProcess';

export interface SharedStream {
  streamId: string;
  streamRevision?: string;
  linked: boolean;
  processIds: string[];
  processes?: SharedProcess[];
  steps: Array<SharedProcess['steps'][number] & { processId: string; measurement?: Record<string, unknown> }>;
  unresolvedReferences: Array<{ stepId: string; reason: string; values?: Record<string, unknown> }>;
  legacy?: unknown;
}

export function useSharedStream(engagementId: string | null, streamId: string | null) {
  const [shared, setShared] = useState<SharedStream>();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const current = useRef('');
  const loadedContext = useRef('');
  const context = `${engagementId}/${streamId}`; current.current = context;
  const pending = useRef(new Map<string, string>());
  const base = engagementId ? `/api/engagements/${encodeURIComponent(engagementId)}` : null;
  const path = base && streamId ? `${base}/streams/${encodeURIComponent(streamId)}` : null;
  const refresh = useCallback(async () => {
    if (!path) return;
    try {
      const response = await fetch(`${path}/shared-processes`);
      if (!response.ok) throw new Error('The shared value stream could not be loaded.');
      const value = await response.json() as SharedStream;
      if (current.current === context) { loadedContext.current = context; setShared(value); setError(null); }
    } catch (failure) { if (current.current === context) setError((failure as Error).message); }
  }, [context, path]);
  useEffect(() => { setShared(undefined); setError(null); void refresh(); }, [refresh]);
  useEffect(() => {
    const reload = () => { void refresh(); };
    window.addEventListener('shared-process-changed', reload);
    return () => window.removeEventListener('shared-process-changed', reload);
  }, [refresh]);
  const post = useCallback(async (url: string, value: Record<string, unknown>) => {
    const signature = `${context}/${url}/${JSON.stringify(value)}`;
    const commandId = pending.current.get(signature) || sharedCommandId();
    pending.current.set(signature, commandId);
    setSaving(true);
    try {
      const response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...value, commandId }) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error?.message || result.error || 'The change was not saved.');
      pending.current.delete(signature);
      await refresh();
      window.dispatchEvent(new Event('shared-process-changed'));
      return true;
    } catch (failure) { if (current.current === context) setError((failure as Error).message); return false; }
    finally { setSaving(false); }
  }, [context, refresh]);
  const command = useCallback(async (processId: string, operation: Record<string, unknown>) => {
    const process = shared?.processes?.find(p => p.processId === processId);
    if (!base || !process || loadedContext.current !== context) { setError('Reload this stream before changing its process.'); return false; }
    return post(`${base}/processes/${encodeURIComponent(processId)}/commands`, { expectedRevision: process.revision, operation });
  }, [base, post, shared, context]);
  const measure = (stepId: string, values: Record<string, unknown>) => path ? post(`${path}/step-measurements`, { stepId, values }) : Promise.resolve(false);
  const link = (processIds: string[], reconcile = false) => path ? post(`${path}/processes`, { processIds, ...(reconcile ? { reconciliation: { strategy: 'preserve-legacy-and-link' } } : {}) }) : Promise.resolve(false);
  return { shared: loadedContext.current === context ? shared : undefined, error, saving, refresh, command, measure, link };
}
